import type { Caption } from '../analysis/structure';
import { editableSegments, type Paragraph } from '../model/document';
import { GOST_2_105, GOST_7_32 } from '../profiles/ids';
import { startsWithUpper } from '../util/text';
import { defineRule, type ByProfile, type FixAction, type Rule, type RuleContext } from './types';
import { hasTrailingPeriod, quote, trailingPeriodFix, upperFirstFix } from './util';

type Kind = 'figure' | 'table';

const WORD: Record<Kind, string> = { figure: 'Рисунок', table: 'Таблица' };
const WORD_PLURAL: Record<Kind, string> = { figure: 'Рисунки', table: 'Таблицы' };
const WORD_ACC: Record<Kind, string> = { figure: 'рисунок', table: 'таблицу' };
const WORD_GEN: Record<Kind, string> = { figure: 'рисунка', table: 'таблицы' };

/** Пункты обоих стандартов для правила. */
const clauses = (gost732: string, gost2105: string): ByProfile => ({
  [GOST_7_32]: `ГОСТ 7.32-2017, ${gost732}`,
  [GOST_2_105]: `ГОСТ Р 2.105-2019, ${gost2105}`,
});

const DASH = '[-‐-―−]';
const NUM = '((?:[А-ЯA-Z]\\.?\\s?)?\\d{1,3}(?:[.-]\\d{1,3})*)';
const CAPTION_RE: Record<Kind, RegExp> = {
  figure: new RegExp(`^(Рисунок|Рис\\.?|Figure|Fig\\.)(\\s*№?\\s*)${NUM}(\\s*[.:)]?\\s*(?:${DASH}\\s*)?)(.*?)\\s*$`, 'sd'),
  table: new RegExp(`^(Таблица|Табл\\.?|Table)(\\s*№?\\s*)${NUM}(\\s*[.:)]?\\s*(?:${DASH}\\s*)?)(.*?)\\s*$`, 'sd'),
};

const REF_NUM = '(?:[А-ЯA-Z]\\.)?\\d{1,3}(?:\\.\\d{1,3})*';
const REF_LIST = `(${REF_NUM}(?:\\s*(?:,|и|или|[–—-])\\s*${REF_NUM})*)`;
const REF_WORDS: Record<Kind, string> = {
  figure: '(?<!\\p{L})(?:рис\\.|рисун(?:ок|ка|ку|ком|ке|ки|ков|кам|ками|ках))',
  table: '(?<!\\p{L})(?:табл\\.|таблиц(?:а|ы|е|у|ей|ой|ам|ами|ах))',
};
const REF_RE: Record<Kind, RegExp> = {
  figure: new RegExp(`${REF_WORDS.figure}\\s*№?\\s*${REF_LIST}`, 'giu'),
  table: new RegExp(`${REF_WORDS.table}\\s*№?\\s*${REF_LIST}`, 'giu'),
};
/** То же с позициями групп — для правок в номерах. */
const REF_RE_D: Record<Kind, RegExp> = {
  figure: new RegExp(REF_RE.figure.source, 'giud'),
  table: new RegExp(REF_RE.table.source, 'giud'),
};

/** Заголовок графы «номер по порядку». */
const NPP_HEADER = /^(?:№|N|No\.?|Nº)\s*(?:п\.?\s*\/?\s*п\.?)?$|^номер(?:\s+по\s+порядку)?$|^порядковый\s+номер$/i;
const NPP_EXPLICIT = /п\s*\/\s*п|п\.\s*п\.|по\s+порядку|порядковый/i;

const normNum = (n: string): string => n.replace(/\s/g, '').replace(/-/g, '.');

function parseCaption(p: Paragraph, kind: Kind): { m: RegExpExecArray; off: number } | undefined {
  const off = p.text.search(/[^\s\ufffc]/);
  if (off < 0) return undefined;
  const m = CAPTION_RE[kind].exec(p.text.slice(off));
  return m ? { m, off } : undefined;
}

function cellText(ctx: RuleContext, table: number, row: number, col: number): string {
  const cell = ctx.model.tables[table]?.rows[row]?.cells[col];
  if (!cell) return '';
  return cell.blocks
    .map((b) => (b.kind === 'paragraph' ? b.text : ''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Проблемы оформления подписи и исправления к ним. */
function captionIssues(ctx: RuleContext, c: Caption): { problems: string[]; fix: FixAction[] } {
  const p = ctx.model.paragraphs[c.paragraph]!;
  const problems: string[] = [];
  const fix: FixAction[] = [];
  const parsed = parseCaption(p, c.kind);
  if (!parsed) return { problems, fix };
  const { m, off } = parsed;
  const ix = m.indices!;
  const at = (g: number): [number, number] => [off + ix[g]![0], off + ix[g]![1]];
  const word = WORD[c.kind];
  const push = (f: FixAction | undefined): void => {
    if (f) fix.push(f);
  };

  if (m[1] !== word || m[2] !== ' ') {
    problems.push(m[1] !== word ? `пишется «${word} ${normNum(m[3]!)}», без сокращений` : m[2]!.includes('№') ? 'знак «№» не нужен' : 'нужен пробел перед номером');
    const [s] = at(1);
    const [, e] = at(2);
    push({ kind: 'text', paragraph: p.index, start: s, end: e, expect: p.text.slice(s, e), replace: `${word} ` });
  }
  const sep = m[4]!;
  const title = m[5]!;
  const dashes = ctx.profile.captions.dashes;
  if (title) {
    const d = new RegExp(DASH).exec(sep)?.[0];
    if (!(d && dashes.includes(d) && sep === ` ${d} `)) {
      problems.push(!d ? 'нет тире между номером и названием' : !dashes.includes(d) ? 'дефис вместо тире' : /[.:)]/.test(sep) ? 'лишний знак после номера' : 'пробелы вокруг тире');
      const [s, e] = at(4);
      push({ kind: 'text', paragraph: p.index, start: s, end: e, expect: sep, replace: ` ${dashes[0] ?? '—'} ` });
    }
    if (!startsWithUpper(title)) {
      problems.push('название со строчной буквы');
      push(upperFirstFix(p, at(5)[0]));
    }
    if (hasTrailingPeriod(title)) {
      problems.push('точка в конце');
      push(trailingPeriodFix(p));
    }
  } else if (sep.trim()) {
    problems.push('лишний знак после номера');
    const [s, e] = at(4);
    push({ kind: 'text', paragraph: p.index, start: s, end: e, expect: sep, replace: '' });
  }

  const first = p.props.indFirst ?? 0;
  const left = p.props.indLeft ?? 0;
  if (c.kind === 'figure') {
    if (p.props.jc !== 'center' || Math.abs(first) > 20 || Math.abs(left) > 20) {
      problems.push(p.props.jc !== 'center' ? 'не по центру' : 'сдвинута отступом');
      push({ kind: 'para', paragraph: p.index, set: { jc: 'center', indFirst: 0, indLeft: 0 } });
    }
  } else if (p.props.jc === 'center' || p.props.jc === 'right' || Math.abs(first) > 20 || Math.abs(left) > 20) {
    problems.push(p.props.jc === 'center' || p.props.jc === 'right' ? 'не слева' : 'с абзацным отступом');
    push({ kind: 'para', paragraph: p.index, set: { jc: 'left', indFirst: 0, indLeft: 0 } });
  }
  if (c.misplaced) problems.push(c.kind === 'figure' ? 'стоит над рисунком, а нужно под ним' : 'стоит под таблицей, а нужно над ней');
  return { problems, fix };
}

function captionRule(kind: Kind): Rule {
  return defineRule({
    id: `objects.${kind}-caption`,
    category: 'objects',
    title: kind === 'figure' ? 'Подписи рисунков' : 'Подписи таблиц',
    severity: 'warning',
    gost: kind === 'figure' ? clauses('п. 6.5.4, 6.5.7', 'п. 6.9.3, 6.9.4') : clauses('п. 6.6.3', 'п. 6.8.1, 6.8.7'),
    why:
      kind === 'figure'
        ? {
            [GOST_7_32]: 'Подпись — «Рисунок 1 — Название»: под рисунком, по центру, с прописной буквы, без точки в конце.',
            [GOST_2_105]: 'Подпись — «Рисунок 1 — Название» под рисунком и пояснительными данными. По центру, с прописной буквы, без точки в конце — так требует большинство методичек.',
          }
        : {
            [GOST_7_32]: 'Подпись — «Таблица 1 — Название»: над таблицей, слева, без абзацного отступа, с прописной буквы, без точки в конце.',
            [GOST_2_105]: 'Слово «Таблица» с номером и наименование через тире пишут над таблицей слева: «Таблица 1 — Название», без точки в конце.',
          },
    check(ctx) {
      for (const c of ctx.s.captions) {
        if (c.kind !== kind || c.continuation) continue;
        const { problems, fix } = captionIssues(ctx, c);
        if (!problems.length) continue;
        const p = ctx.model.paragraphs[c.paragraph]!;
        ctx.report({ paragraph: c.paragraph, message: `Подпись ${quote(p.text, 45)}: ${problems.join(', ')}`, fix, fixLabel: 'Оформить подпись' });
      }
    },
  });
}

function missingRule(kind: Kind): Rule {
  return defineRule({
    id: `objects.${kind}-missing`,
    category: 'objects',
    title: kind === 'figure' ? 'Рисунки без подписи' : 'Таблицы без номера',
    severity: 'warning',
    aggregate: kind === 'figure' ? ['рисунок', 'рисунка', 'рисунков'] : ['таблица', 'таблицы', 'таблиц'],
    gost: kind === 'figure' ? clauses('п. 6.5.4, 6.5.7', 'п. 6.9.3') : clauses('п. 6.6.3, 6.6.4', 'п. 6.8.2'),
    why:
      kind === 'figure'
        ? {
            [GOST_7_32]: 'Каждый рисунок нумеруют и подписывают: без номера на него нельзя сослаться.',
            [GOST_2_105]: 'Рисунки нумеруют; без номера допускается оставить только небольшие рисунки в тексте, на которые нет ссылок.',
          }
        : 'Каждую таблицу нумеруют: над ней пишут «Таблица N — Наименование».',
    check(ctx) {
      const paras = ctx.model.paragraphs;
      const targeted = new Set(ctx.s.captions.filter((c) => c.kind === kind && c.target !== undefined).map((c) => c.target!));
      if (kind === 'table') {
        for (const t of ctx.s.dataTables) {
          if (targeted.has(t)) continue;
          const table = ctx.model.tables[t]!;
          ctx.report({ paragraph: table.firstParagraph, message: 'Таблица без номера — над ней нужна строка «Таблица N — Наименование»' });
        }
        return;
      }
      const dataTables = new Set(ctx.s.dataTables);
      for (const idx of ctx.s.figures) {
        if (targeted.has(idx) || ctx.s.captionByPara.has(idx)) continue;
        const p = paras[idx]!;
        if (p.cell && dataTables.has(p.cell.table)) continue;
        // Несколько рисунков подряд с общей подписью (а, б).
        let k = idx + 1;
        let shared = false;
        while (k < paras.length && (paras[k]!.isEmpty || ctx.s.figures.includes(k))) {
          if (targeted.has(k)) shared = true;
          k++;
        }
        if (shared) continue;
        ctx.report({ paragraph: idx, message: 'Рисунок без подписи — под ним нужна строка «Рисунок N — Название»' });
      }
    },
  });
}

/** Номер раздела (первого уровня), в котором находится абзац. */
function sectionNumberAt(ctx: RuleContext, idx: number): string | undefined {
  let num: string | undefined;
  for (const h of ctx.s.headings) {
    if (h.paragraph > idx) break;
    if (h.kind === 'section' && h.level === 1 && h.number) num = h.number.split('.')[0];
  }
  return num;
}

function appendixLetterAt(ctx: RuleContext, idx: number): string | undefined {
  let letter: string | undefined;
  for (const h of ctx.s.headings) {
    if (h.paragraph > idx) break;
    if (h.kind === 'appendix') letter = h.appendixLetter;
  }
  return letter;
}

/** Номер, который подпись должна иметь по порядку: сквозной, в пределах раздела или с буквой приложения. */
function expectedNumbers(ctx: RuleContext, caps: Caption[]): { expected: Map<Caption, string>; perSection?: boolean } {
  const expected = new Map<Caption, string>();
  let perSection: boolean | undefined;
  let counter = 0;
  let currentSec: string | undefined;
  let secCounter = 0;
  let appLetter: string | undefined;
  let appCounter = 0;
  for (const c of caps) {
    const num = normNum(c.number);
    if (ctx.s.regions[c.paragraph] === 'appendix') {
      const letter = appendixLetterAt(ctx, c.paragraph) ?? /^[А-ЯA-Z]/.exec(num)?.[0] ?? 'А';
      if (letter !== appLetter) {
        appLetter = letter;
        appCounter = 0;
      }
      expected.set(c, `${letter}.${++appCounter}`);
      continue;
    }
    perSection ??= /^\d+\.\d+$/.test(num);
    if (perSection) {
      const sec = sectionNumberAt(ctx, c.paragraph) ?? num.split('.')[0]!;
      if (sec !== currentSec) {
        currentSec = sec;
        secCounter = 0;
      }
      expected.set(c, `${sec}.${++secCounter}`);
    } else {
      expected.set(c, String(++counter));
    }
  }
  return { expected, perSection };
}

/**
 * Перенумерация: номера в подписях и в ссылках на них («на рисунке 3», «табл. 2, 4»).
 * Номера в полях SEQ и перекрёстных ссылках Word обновит сам — для них включаем обновление полей.
 */
function renumberFix(ctx: RuleContext, kind: Kind, caps: Caption[], expected: Map<Caption, string>): FixAction[] {
  const paras = ctx.model.paragraphs;
  const actions: FixAction[] = [];
  let fields = false;
  // «2-1» остаётся с дефисом, если так пронумерован весь документ.
  const like = (orig: string, next: string): string => (orig.includes('-') && !orig.includes('.') ? next.replace('.', '-') : next);
  const editable = (p: Paragraph, start: number, end: number): boolean => !!editableSegments(p, start, end);
  const push = (p: Paragraph, start: number, end: number, next: string): void => {
    if (!editable(p, start, end)) {
      fields = true;
      return;
    }
    const orig = p.text.slice(start, end);
    actions.push({ kind: 'text', paragraph: p.index, start, end, expect: orig, replace: like(orig, next) });
  };

  for (const c of caps) {
    const next = expected.get(c)!;
    if (normNum(c.number) === next) continue;
    const p = paras[c.paragraph]!;
    const parsed = parseCaption(p, kind);
    const ix = parsed?.m.indices?.[3];
    if (parsed && ix) push(p, parsed.off + ix[0], parsed.off + ix[1], next);
  }

  const byOld = new Map<string, Caption[]>();
  for (const c of caps) byOld.set(normNum(c.number), [...(byOld.get(normNum(c.number)) ?? []), c]);
  for (const p of paras) {
    const region = ctx.s.regions[p.index];
    const role = ctx.s.roles[p.index];
    if (p.textBox || (region !== 'main' && region !== 'appendix') || role === 'caption' || role === 'heading' || role === 'code') continue;
    for (const m of p.text.matchAll(REF_RE_D[kind])) {
      const listStart = m.indices![1]![0];
      for (const t of m[1]!.matchAll(/(?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})*/g)) {
        const candidates = byOld.get(normNum(t[0]));
        if (!candidates) continue;
        // Одинаковые номера у разных подписей: ссылка относится к ближайшей подписи после неё.
        const target = candidates.find((c) => c.paragraph >= p.index) ?? candidates[candidates.length - 1]!;
        const next = expected.get(target)!;
        if (normNum(t[0]) === next && candidates.length === 1) continue;
        if (normNum(t[0]) !== next) push(p, listStart + t.index, listStart + t.index + t[0].length, next);
      }
    }
  }
  if (fields) actions.push({ kind: 'updateFields' });
  return actions;
}

function numberingRule(kind: Kind): Rule {
  const word = WORD[kind];
  return defineRule({
    id: `objects.${kind}-numbering`,
    category: 'objects',
    title: kind === 'figure' ? 'Нумерация рисунков' : 'Нумерация таблиц',
    severity: 'warning',
    gost: kind === 'figure' ? clauses('п. 6.5.4–6.5.6', 'п. 6.9.3') : clauses('п. 6.6.4', 'п. 6.8.2'),
    why: 'Нумерация сквозная (1, 2, 3) или в пределах раздела (2.1, 2.2); в приложениях — с буквой приложения (А.1). Если номера сбились, исправление перенумерует подписи и ссылки на них в тексте.',
    check(ctx) {
      const caps = ctx.s.captions.filter((c) => c.kind === kind && !c.continuation);
      if (!caps.length) return;
      const { expected, perSection } = expectedNumbers(ctx, caps);
      const changed = caps.filter((c) => normNum(c.number) !== expected.get(c));
      if (changed.length) {
        const seen = new Set<string>();
        const duplicate = caps.find((c) => seen.size === seen.add(normNum(c.number)).size);
        const pairs = changed
          .slice(0, 4)
          .map((c) => `${normNum(c.number)} → ${expected.get(c)}`)
          .join(', ');
        const fix = renumberFix(ctx, kind, caps, expected);
        ctx.report({
          paragraph: changed[0]!.paragraph,
          message:
            `${WORD_PLURAL[kind]} пронумерованы не по порядку` +
            (duplicate ? ` (номер «${word} ${normNum(duplicate.number)}» повторяется)` : '') +
            `: ${pairs}${changed.length > 4 ? ` и ещё ${changed.length - 4}` : ''}`,
          fix: fix.length ? fix : undefined,
          fixLabel: `Перенумеровать ${kind === 'figure' ? 'рисунки' : 'таблицы'} и ссылки на них`,
        });
      }
      const mode = ctx.profile.captions.numbering;
      if (perSection && mode === 'continuous') {
        ctx.report({ severity: 'info', paragraph: caps[0]!.paragraph, message: `${WORD_PLURAL[kind]} пронумерованы в пределах разделов, а методичка требует сквозную нумерацию` });
      } else if (perSection === false && mode === 'section') {
        ctx.report({ severity: 'info', paragraph: caps[0]!.paragraph, message: `${WORD_PLURAL[kind]} пронумерованы сквозной нумерацией, а методичка требует нумерацию в пределах раздела` });
      }
    },
  });
}

/** Номер → индекс абзаца первой ссылки на него в тексте. */
function collectRefs(ctx: RuleContext, kind: Kind): Map<string, number> {
  const refs = new Map<string, number>();
  for (const p of ctx.model.paragraphs) {
    const region = ctx.s.regions[p.index];
    const role = ctx.s.roles[p.index];
    if (p.textBox || (region !== 'main' && region !== 'appendix') || role === 'caption' || role === 'heading' || role === 'code') continue;
    const re = REF_RE[kind];
    re.lastIndex = 0;
    for (const m of p.text.matchAll(re)) {
      for (const part of m[1]!.split(/\s*(?:,|или|и)\s*/)) {
        const range = /^((?:[А-ЯA-Z]\.)?(?:\d{1,3}\.)*)(\d{1,3})\s*[–—-]\s*((?:[А-ЯA-Z]\.)?(?:\d{1,3}\.)*)(\d{1,3})$/.exec(part);
        if (range && range[1] === range[3]) {
          for (let k = Number(range[2]); k <= Number(range[4]) && k - Number(range[2]) < 50; k++) {
            const key = `${range[1]}${k}`;
            if (!refs.has(key)) refs.set(key, p.index);
          }
        } else {
          const key = normNum(part);
          if (key && !refs.has(key)) refs.set(key, p.index);
        }
      }
    }
  }
  return refs;
}

function referenceRule(kind: Kind): Rule {
  return defineRule({
    id: `objects.${kind}-ref`,
    category: 'objects',
    title: kind === 'figure' ? 'Ссылки на рисунки' : 'Ссылки на таблицы',
    severity: 'warning',
    gost: kind === 'figure' ? clauses('п. 6.5.1', 'п. 6.9.3') : clauses('п. 6.6.2', 'п. 6.8.3, 6.8.6'),
    why:
      kind === 'figure'
        ? {
            [GOST_7_32]: 'На все иллюстрации дают ссылки в тексте («в соответствии с рисунком 2»). Рисунок располагают после первого упоминания.',
            [GOST_2_105]: 'На рисунки ссылаются так: «в соответствии с рисунком 2». Рисунок располагают возможно ближе к тексту, где он упомянут.',
          }
        : {
            [GOST_7_32]: 'На все таблицы должны быть ссылки («в таблице 1»). Таблицу располагают после первого упоминания.',
            [GOST_2_105]: 'На все таблицы должны быть ссылки со словом «таблица» и номером. Таблицу помещают под текстом, в котором впервые дана ссылка на неё.',
          },
    check(ctx) {
      const refs = collectRefs(ctx, kind);
      for (const c of ctx.s.captions) {
        if (c.kind !== kind || c.continuation) continue;
        const num = normNum(c.number);
        const first = refs.get(num);
        if (first === undefined) {
          ctx.report({ paragraph: c.paragraph, message: `На ${WORD_ACC[kind]} ${num} нет ссылки в тексте` });
        } else {
          const anchor = kind === 'figure' ? Math.min(c.target ?? c.paragraph, c.paragraph) : c.paragraph;
          if (first > anchor) {
            ctx.report({
              severity: 'info',
              paragraph: c.paragraph,
              message: `${WORD[kind]} ${num} впервые упоминается в тексте только после самого ${WORD_GEN[kind]}`,
            });
          }
        }
      }
    },
  });
}

export const objectRules: Rule[] = [
  captionRule('figure'),
  missingRule('figure'),
  numberingRule('figure'),
  referenceRule('figure'),
  captionRule('table'),
  missingRule('table'),
  numberingRule('table'),
  referenceRule('table'),

  defineRule({
    id: 'objects.table-number-column',
    category: 'objects',
    title: 'Графа «№ п/п» в таблицах',
    severity: 'warning',
    gost: 'ГОСТ Р 2.105-2019, п. 6.8.8',
    why: 'Графу «Номер по порядку» в таблицу не включают. Если показатели нужно пронумеровать, номера ставят в первой графе (боковике) перед их наименованием.',
    check(ctx) {
      for (const t of ctx.s.dataTables) {
        const table = ctx.model.tables[t]!;
        const head = cellText(ctx, t, 0, 0);
        if (!NPP_HEADER.test(head)) continue;
        // Одиночный «№» бывает и номером варианта; графа «по порядку» — это 1, 2, 3 подряд.
        const values = table.rows.slice(1, 6).map((_, i) => cellText(ctx, t, i + 1, 0));
        const sequential = values.length >= 2 && values.every((v, i) => v === String(i + 1) || v === `${i + 1}.`);
        if (!NPP_EXPLICIT.test(head) && !sequential) continue;
        ctx.report({
          paragraph: table.firstParagraph,
          message: `Графа ${quote(head, 20)} с порядковыми номерами — такую графу в таблицу не включают, номер ставят перед наименованием в первой графе`,
        });
      }
    },
  }),

  defineRule({
    id: 'objects.formula-number',
    category: 'objects',
    title: 'Нумерация формул',
    severity: 'info',
    gost: clauses('п. 6.8.3', 'п. 6.10.4'),
    why: 'Формулы располагают посередине строки и нумеруют арабскими цифрами в круглых скобках у правого края: (1).',
    check(ctx) {
      const formulas = ctx.s.formulas.filter((f) => ctx.s.regions[f.paragraph] === 'main' || ctx.s.regions[f.paragraph] === 'appendix');
      let unnumbered = 0;
      for (const f of formulas) {
        if (f.number || unnumbered++ >= 10) continue;
        ctx.report({ paragraph: f.paragraph, message: 'Формула без номера — номер ставят в круглых скобках у правого края строки: (1)' });
      }
      const plain = formulas.filter((f) => f.number && /^\d+$/.test(f.number));
      let expected = 1;
      for (const f of plain) {
        const n = Number(f.number);
        if (n !== expected) {
          ctx.report({ severity: 'warning', paragraph: f.paragraph, message: `Формула (${n}): ожидался номер (${expected})` });
          break;
        }
        expected++;
      }
    },
  }),

  defineRule({
    id: 'objects.formula-where',
    category: 'objects',
    title: 'Пояснения к формулам',
    severity: 'warning',
    gost: clauses('п. 6.8.2', 'п. 6.10.1'),
    why: 'Пояснение символов начинают со слова «где» без двоеточия; каждый символ — с новой строки.',
    check(ctx) {
      for (const p of ctx.model.paragraphs) {
        if (ctx.s.roles[p.index] !== 'formula-where') continue;
        const m = /^(\s*)([Гг]де)(\s*:)?/.exec(p.text);
        if (!m) continue;
        const problems: string[] = [];
        const fix: FixAction[] = [];
        const at = m[1]!.length;
        if (m[2] === 'Где') {
          problems.push('«где» пишут со строчной буквы');
          fix.push({ kind: 'text', paragraph: p.index, start: at, end: at + 1, expect: 'Г', replace: 'г' });
        }
        if (m[3]) {
          problems.push('двоеточие после «где» не ставят');
          const s = at + m[2]!.length;
          fix.push({ kind: 'text', paragraph: p.index, start: s, end: s + m[3].length, expect: m[3], replace: '' });
        }
        if (problems.length) ctx.report({ paragraph: p.index, message: `Пояснение к формуле: ${problems.join(', ')}`, fix, fixLabel: 'Исправить «где»' });
      }
    },
  }),
];
